import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getGlobalCandidates,
  postInviteforGlobal,
} from "../../../Redux/Reducers/JobReducer";
import {
  Box,
  Avatar,
  Typography,
  Chip,
  Badge,
  TextField,
  Grid,
  Pagination,
  CircularProgress,
  Modal,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Tooltip,
} from "@mui/material";
import CandidateResumeModal from "./SideFeedComponents/CandidateResumeModal";
import { useQuantityContext } from "../../../Components/QuantityContext";
import GlobalSearchFilter from "./GlobalSearchFilter";
import { useLocation, useParams } from "react-router-dom";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const InviteModal = ({ open, onClose, companies, selectedCandidate }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { openSnackbar } = useQuantityContext();
  const { id } = useParams();

  const handleCompanySelect = async (jobId) => {
    try {
      // Validate required fields
      if (!selectedCandidate?.email || !jobId) {
        openSnackbar("Missing required information", "error");
        return;
      }

      const payload = {
        candidate_email: selectedCandidate.email,
        candidate_name: `${selectedCandidate.first_name} ${selectedCandidate.last_name}`,
        candidate_id: selectedCandidate.reference_id,
        job_id: jobId,
      };

      // Dispatch the API call and wait for response
      const result = await dispatch(postInviteforGlobal(payload)).unwrap();

      // Handle success
      openSnackbar(
        result.message || "Invitation sent successfully!",
        "success"
      );
      console.log(`Invited ${payload.candidate_name} to job ${jobId}`);
    } catch (error) {
      // Handle API errors
      console.error("Invitation failed:", error);
      openSnackbar(error.message || "Already invited to this job", "error");
    } finally {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          ...style,
          width: { xs: "90%", sm: 400 },
          p: { xs: 2, sm: 4 },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
          }}
        >
          Select Job to Invite
        </Typography>
        <List sx={{ maxHeight: { xs: "50vh", sm: "300px" }, overflow: "auto" }}>
          {companies?.map((job) => (
            <ListItem
              button
              key={job.id}
              onClick={() => handleCompanySelect(job.id)}
              sx={{
                py: {
                  xs: 0.5,
                  sm: 1,
                  border: "1px solid gray",
                  marginTop: "10px",
                  borderRadius: "5px",
                },
              }}
            >
              <ListItemAvatar>
                <Avatar
                  src={job.company_logo}
                  alt={job.company_name}
                  sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}
                >
                  {job.company_name?.[0]}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={job.company_name}
                secondary={job.role}
                primaryTypographyProps={{
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
                secondaryTypographyProps={{
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                }}
              />
            </ListItem>
          ))}
        </List>
        <Button
          onClick={onClose}
          sx={{
            mt: 2,
            float: "right",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Modal>
  );
};

const GlobalSearchCandidates = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const GlobalCandidatesData = useSelector(
    (state) => state?.jobs?.getGlobalCandidates
  );
  const GlobalCandidates = GlobalCandidatesData?.data;
  const [selectedAssignment, setSelectedAssignment] = useState("");
  const [openPdf, setPdfOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const Jobs = useSelector((state) => state?.jobs?.postedJobsData);
  const PostedJobs = Jobs?.posted_jobs || [];
  const selectedCompany =
    useSelector((state) => state?.jobs?.selectedCompany) || {};
  const itemsPerPage = 10;
  const [appliedFilters, setAppliedFilters] = useState({
    experience: [],
    skills: [],
    location: [],
    mode: [],
  });
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const lastSegment = pathSegments[pathSegments?.length - 1];

  // Check if it's a valid numeric ID
  const dynamicId = /^\d+$/.test(lastSegment) ? Number(lastSegment) : null;

  // Filter jobs only if dynamicId exists
  const filteredJobs = dynamicId
    ? PostedJobs?.filter((job) => job?.id === dynamicId)
    : PostedJobs;

  // console.log("GlobalCandidatesData", GlobalCandidatesData);

  useEffect(() => {
    dispatch(getGlobalCandidates(searchTerm));
  }, [searchTerm]);

  // Filter candidates by first_name and designation

  const filteredCandidates =
    GlobalCandidates?.filter((candidate) => {
      if (!searchTerm) return true;
      return (
        candidate?.first_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate?.designation
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate?.company?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }) || [];

  // Pagination logic
  const paginatedCandidates = filteredCandidates?.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
  const totalPages = Math.ceil(filteredCandidates?.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Show loading indicator if data is not yet available
  if (!GlobalCandidates) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleCandidateClick = async (candidate) => {
    // console.log("candidate", candidate);
    setSelectedAssignment(candidate);
    setPdfOpen(true);
  };

  const handleCloseModal = () => {
    setPdfOpen(false);
  };

  const handleInviteClick = (candidate, e) => {
    e.stopPropagation();
    setSelectedCandidate(candidate);
    setInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setInviteModalOpen(false);
    setSelectedCandidate(null);
  };

  const handleApplyFilter = (filters) => {
    setAppliedFilters(filters);
    // Update your API call or filtering logic here
    const params = {
      ...filters,
      search: searchTerm,
    };
    dispatch(getGlobalCandidates(params));
  };

  const handleClearFilter = () => {
    setAppliedFilters({
      experience: [],
      skills: [],
      location: [],
      mode: [],
    });
    // Reset to original data
    dispatch(getGlobalCandidates(searchTerm));
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "white",
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
        borderRadius: "5px",
        maxHeight: "90vh",
        overflowY: "scroll",
        scrollbarWidth: "none",
        boxShadow: 3,
      }}
    >
      <Typography sx={{ color: "black", fontWeight: "bold", mb: 2 }}>
        Search Candidates
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          justifyContent: "space-evenly",
          alignItems: "center",
        }}
      >
        <TextField
          label="Search by Name or Designation"
          variant="outlined"
          fullWidth
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />
        <GlobalSearchFilter
          onApplyFilter={handleApplyFilter}
          onClearFilter={handleClearFilter}
        />
      </Box>

      <Grid container spacing={1} justifyContent="center">
        {paginatedCandidates?.map((candidate) => (
          <Grid item xs={12} md={6} key={candidate?.id}>
            <Box
              sx={{
                width: "140px", // fixed width
                height: "180px", // fixed height
                p: 2,
                borderRadius: 2,
                border: "2px solid #00000020",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#fff",
                m: "auto",
                transition: "all 0.3s ease",
                "&:hover": {
                  border: "1px solid #0044C6",
                },
              }}
              onClick={() => handleCandidateClick(candidate)}
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <Avatar
                    src={
                      candidate?.logo ||
                      "https://i.pinimg.com/736x/ec/d9/c2/ecd9c2e8ed0dbbc96ac472a965e4afda.jpg"
                    }
                    sx={{ width: 24, height: 24, border: "2px solid white" }}
                  />
                }
              >
                <Avatar
                  src={candidate?.profile_url}
                  alt={candidate?.first_name}
                  sx={{ width: 60, height: 60, mb: 1 }}
                >
                  {candidate?.first_name?.[0] || "?"}
                </Avatar>
              </Badge>
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ color: "black", fontWeight: "bold" }}>
                  {candidate?.first_name}
                </Typography>
                <Tooltip title={candidate?.designation || ""}>
                  <Typography
                    sx={{
                      color: "black",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 100, // adjust as needed
                    }}
                  >
                    {candidate?.designation}
                  </Typography>
                </Tooltip>

                <Tooltip title={candidate?.company || ""}>
                  <Typography
                    sx={{
                      color: "black",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 100, // adjust as needed
                    }}
                  >
                    {candidate?.company}
                  </Typography>
                </Tooltip>
              </Box>
              <Chip
                label="Invite"
                color="success"
                variant="outlined"
                sx={{ mt: 1, cursor: "pointer" }}
                onClick={(e) => handleInviteClick(candidate, e)}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      <CandidateResumeModal
        openPdf={openPdf}
        onClose={handleCloseModal}
        candidate={selectedAssignment}
      />

      <InviteModal
        open={inviteModalOpen}
        onClose={handleCloseInviteModal}
        companies={filteredJobs}
        selectedCandidate={selectedCandidate}
      />
    </Box>
  );
};

export default GlobalSearchCandidates;
